import { Button } from '@design/components/button/Button';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { useTouchDevice } from '@terminal/hooks/MediaQueryHook';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useScheduleDrawer } from '@terminal/stores/use-schedule-drawer/useScheduleDrawer';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { useUserUpload } from '@terminal/views/alfred/research/use-user-upload/useUserUpload';
import { useTranslation } from 'react-i18next';
import { ChatInputConfig } from '../../chat-input';
import styles from './ChatActionButtons.module.scss';

export interface ChatActionButtonsProps {
  isMessagePending: boolean;
  inputValue: string;
  abortRequest?: () => void;
  config?: ChatInputConfig;
  showRefineButton?: boolean;
  onRefineClick?: () => void;
}

export const ChatActionButtons = ({
  isMessagePending,
  inputValue,
  abortRequest,
  config,
  showRefineButton = false,
  onRefineClick,
}: ChatActionButtonsProps) => {
  const { t } = useTranslation('chat');
  const isTouchDevice = useTouchDevice();
  const isAlfredEverywhere = config?.alfredEverywhere;
  const openScheduleDrawer = useScheduleDrawer(state => state.open);

  const analysisMode = useChat(state => state.analysisMode);

  const isScheduledTaskEnabled = useFlags(state =>
    state.getFeatureFlagState('scheduled_task_feature')
  );
  const isAlfredAskAnalystEnabled = useFlags(state =>
    state.getFeatureFlagState('alfred_ask_analyst')
  );

  const { uploadingFiles, hasUploadedFiles } = useUserUpload(state => ({
    uploadingFiles: state.files.filter(file => file.status === 'uploading'),
    hasUploadedFiles: state.files.length > 0,
  }));

  const hasEmptyInput = !inputValue.trim();
  const hasUploadingFiles = uploadingFiles.length > 0;
  const isSubmitButtonDisabled =
    hasEmptyInput ||
    hasUploadingFiles ||
    (isAlfredEverywhere && isMessagePending);

  const isScheduleButtonDisabled = hasEmptyInput || hasUploadedFiles;

  const handleScheduleClick = () => {
    openScheduleDrawer(inputValue);
  };

  const getScheduleTooltipLabel = () => {
    if (hasUploadedFiles) {
      return t('chat:scheduleTaskDisabledWithFile');
    }

    if (hasEmptyInput) {
      return t('chat:scheduleTaskDisabledNoInput');
    }

    return t('chat:historySidebar.scheduleTask');
  };

  const refineButton = showRefineButton && !isTouchDevice && (
    <Button
      className={styles.refineButton}
      type="button"
      variant="empty"
      size="medium"
      iconName="Alfred"
      label={t('chat:promptRefinement.button')}
      onClick={onRefineClick}
      disabled={isSubmitButtonDisabled}
      data-testid="prompt-refinement-button"
    />
  );

  const submitButton = (
    <TooltipContainer
      label={
        isMessagePending
          ? t('chat:stopResponse')
          : isAlfredAskAnalystEnabled
            ? t('chat:analysisMode.sendToAnalyst')
            : null
      }
      inPortal
      trigger={[TooltipTrigger.Hover]}
      disabled={analysisMode === 'Auto'}
    >
      {isMessagePending && !isAlfredEverywhere ? (
        <Button
          className={styles.submitButton}
          type="button"
          data-testid="abort-button"
          iconName="Stop"
          onClick={abortRequest}
        />
      ) : (
        <Button
          className={styles.submitButton}
          type="submit"
          data-testid="submit-button"
          variant="primary"
          iconName="ArrowUp"
          disabled={isSubmitButtonDisabled}
        />
      )}
    </TooltipContainer>
  );

  if (!isScheduledTaskEnabled) {
    return (
      <div className={styles.wrapper}>
        {refineButton}
        {submitButton}
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {refineButton}
      <div className={styles.actionButtonsContainer}>
        <TooltipContainer
          label={getScheduleTooltipLabel()}
          inPortal
          trigger={[TooltipTrigger.Hover]}
        >
          <Button
            className={styles.scheduleButton}
            type="button"
            data-testid="schedule-task-button"
            variant="tertiary"
            iconName="Clock"
            disabled={isScheduleButtonDisabled}
            onClick={handleScheduleClick}
          />
        </TooltipContainer>
        {submitButton}
      </div>
    </div>
  );
};
